import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api, API } from "../lib/api";

export function GuidedReader({ slug, start = 0, onPos }) {
  const [d, setD] = useState(null);
  const [k, setK] = useState(start);
  const [box, setBox] = useState({ w: 360, h: 560 });
  const ref = useRef(null);
  useEffect(() => { api.get(`/public/books/${slug}/comic/guided`).then((r) => setD(r.data)).catch(() => setD({ pages: [] })); }, [slug]);
  useEffect(() => {
    const fit = () => ref.current && setBox({ w: ref.current.clientWidth, h: ref.current.clientHeight });
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [d]);
  const steps = useMemo(() => (d?.pages || []).flatMap((p, pi) => [...p.panels.map((r) => ({ pi, r })), ...(p.panels.length > 1 ? [{ pi, r: [0, 0, p.width, p.height] }] : [])]), [d]);
  useEffect(() => { if (steps.length) onPos?.({ mode: "zoom", step: Math.min(k, steps.length - 1) }); }, [k, steps, onPos]);
  if (!d) return <div className="h-96" />;
  if (!steps.length) return <p className="text-center text-slate-400 py-20" data-testid="guided-empty">The author hasn't shared a preview yet.</p>;
  const rtl = d.direction === "rtl";
  const st = steps[Math.min(k, steps.length - 1)];
  const page = d.pages[st.pi];
  const [x0, y0, x1, y1] = st.r;
  const sc = Math.min(box.w / (x1 - x0), box.h / (y1 - y0)) * 0.96;
  const tx = box.w / 2 - ((x0 + x1) / 2) * sc, ty = box.h / 2 - ((y0 + y1) / 2) * sc;
  const go = (n) => setK((v) => Math.min(steps.length - 1, Math.max(0, v + n)));
  return (
    <div data-testid="guided-reader">
      <div ref={ref} className="relative mx-auto w-full max-w-[720px] h-[72vh] overflow-hidden bg-black rounded select-none"
        onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); const left = e.clientX < r.left + r.width / 2; go(left === rtl ? 1 : -1); }}>
        <img src={`${API}/public/books/${slug}/comic/pages/${page.page}`} alt={`Page ${page.page}`} draggable={false}
          style={{ position: "absolute", left: 0, top: 0, width: page.width, height: page.height, maxWidth: "none", transformOrigin: "0 0", transform: `translate(${tx}px, ${ty}px) scale(${sc})`, transition: "transform 0.55s cubic-bezier(.4,0,.2,1)" }}
          data-testid="guided-page" />
      </div>
      <div className="flex items-center justify-center gap-6 mt-3 text-slate-400">
        <button onClick={() => go(rtl ? 1 : -1)} aria-label="Left" data-testid="guided-left"><ChevronLeft size={28} /></button>
        <span className="text-xs" data-testid="guided-position">Page {page.page} · {st.r[2] - st.r[0] === page.width && page.panels.length > 1 ? "whole page" : `panel ${page.panels.findIndex((r) => r.join() === st.r.join()) + 1} of ${page.panels.length}`}</span>
        <button onClick={() => go(rtl ? -1 : 1)} aria-label="Right" data-testid="guided-right"><ChevronRight size={28} /></button>
      </div>
    </div>
  );
}
